import { useState } from 'react';

interface KaraokeFaqProps {
  lang: 'en' | 'ja';
}

export default function KaraokeFaq({ lang }: KaraokeFaqProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const content = {
    en: {
      badge: 'FAQ',
      title: 'Frequently Asked Questions',
      faqs: [
        {
          question: 'Do I need to speak Japanese?',
          answer: 'Not at all! We\'ll handle all communication with the staff. Many karaoke machines also have English interfaces and song search.'
        },
        {
          question: 'What kind of songs can I sing?',
          answer: 'Japanese karaoke has a huge selection: J-Pop, anime songs, K-Pop, Western hits, and more. We\'ll help you find songs you\'ll enjoy.'
        },
        {
          question: 'Can I bring my own drinks or food?',
          answer: 'Most karaoke venues don\'t allow outside food or drinks. They have their own menu with snacks, meals, and beverages available for purchase.'
        },
        {
          question: 'How do I book?',
          answer: 'Simply contact us via WhatsApp with your preferred date, time, and number of people. We\'ll confirm availability and reserve the room for you.'
        },
        {
          question: 'Can I cancel or reschedule?',
          answer: 'Yes, please let us know at least 24 hours in advance. We\'ll do our best to accommodate changes to your booking.'
        },
        {
          question: 'Will you sing with us?',
          answer: 'Absolutely! We\'ll join in and help create a fun atmosphere. But if you prefer to sing solo or with your group, that\'s totally fine too.'
        }
      ]
    },
    ja: {
      badge: 'よくある質問',
      title: 'よくある質問',
      faqs: [
        {
          question: '日本語を話せなくても大丈夫ですか？',
          answer: '全く問題ありません！スタッフとのやり取りはすべて私たちが対応します。多くのカラオケ機器には英語インターフェースや曲検索機能もあります。'
        },
        {
          question: 'どんな曲が歌えますか？',
          answer: '日本のカラオケには膨大な選曲があります：J-Pop、アニメソング、K-Pop、洋楽ヒット曲など。お楽しみいただける曲を見つけるお手伝いをします。'
        },
        {
          question: '飲食物の持ち込みはできますか？',
          answer: 'ほとんどのカラオケ店では外部からの飲食物の持ち込みは禁止されています。店内にスナック、食事、飲み物のメニューがあります。'
        },
        {
          question: '予約方法は？',
          answer: 'WhatsAppでご希望の日時と人数をお知らせください。空き状況を確認し、お部屋を予約いたします。'
        },
        {
          question: 'キャンセルや変更はできますか？',
          answer: 'はい、少なくとも24時間前にお知らせください。予約の変更にできる限り対応いたします。'
        },
        {
          question: '一緒に歌ってくれますか？',
          answer: 'もちろんです！一緒に歌って楽しい雰囲気を作ります。でも、ソロやグループだけで歌いたい場合も全く問題ありません。'
        }
      ]
    }
  };

  const t = content[lang];

  return (
    <section className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 rounded-full mb-6">
            <i className="ri-question-line text-purple-600"></i>
            <span className="text-purple-700 text-sm font-semibold tracking-wide uppercase">{t.badge}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
            {t.title}
          </h2>
        </div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {t.faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl overflow-hidden border border-purple-100 shadow-sm hover:shadow-md transition-shadow duration-300"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-8 py-6 flex items-center justify-between gap-4 text-left hover:bg-white/50 transition-colors duration-300"
              >
                <span className="text-lg font-bold text-gray-900 flex-1">
                  {faq.question}
                </span>
                <i className={`ri-arrow-down-s-line text-2xl text-purple-600 transition-transform duration-300 flex-shrink-0 ${openIndex === index ? 'rotate-180' : ''}`}></i>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-8 pb-6 text-gray-600 leading-relaxed">
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-12 text-center bg-gradient-to-r from-purple-100 to-pink-100 rounded-2xl p-8">
          <i className="ri-chat-smile-3-line text-4xl text-purple-600 mb-4"></i>
          <p className="text-gray-700 text-lg mb-6">
            {lang === 'en' 
              ? 'Still have questions? We\'re here to help!'
              : 'まだ質問がありますか？お気軽にお問い合わせください！'
            }
          </p>
          <button
            onClick={() => {
              const message = lang === 'en' 
                ? 'Hi! I have a question about Tokyo Karaoke Night.'
                : 'こんにちは！Tokyo Karaoke Nightについて質問があります。';
              window.open(`https://wa.me/818041800418?text=${encodeURIComponent(message)}`, '_blank');
            }}
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold rounded-full hover:shadow-lg transition-all duration-300 hover:scale-105 whitespace-nowrap"
          >
            <i className="ri-whatsapp-line text-xl"></i>
            <span>{lang === 'en' ? 'Ask on WhatsApp' : 'WhatsAppで質問'}</span>
          </button>
        </div>
      </div>
    </section>
  );
}